import { getToolById } from "../registry"
import { loadToolConfig } from "./storage"
import { summariseSchema, modelSupportsTemperature } from "./schema-utils"

/**
 * Generate an implementation-ready Markdown writeup of a flow: a Mermaid
 * diagram, every node's config (model, prompts, expected outputs, schema), and
 * every connection's routing condition. Tool-bound agents resolve their live
 * `ai_tooling/{id}` config; placeholder agents use their inline draft.
 *
 * Plain hyphens only - this is user-facing copy.
 */

const TYPE_LABELS = {
	trigger: "Trigger",
	agent: "Agent",
	router: "Router",
	step: "Deterministic step",
	note: "Note",
}

// Mermaid-safe node id and label.
const mermaidId = (id) => `n_${String(id).replace(/[^a-zA-Z0-9_]/g, "_")}`
const escapeLabel = (text) => String(text || "").replace(/"/g, "'").replace(/\s+/g, " ").trim()

// Short edge label for the diagram (mirrors the canvas edge label).
const shortCondition = (data) => {
	const c = data?.condition
	if (!c || c.kind === "always") return data?.note || ""
	if (c.kind === "classification") return c.value || "classification"
	if (c.kind === "content_type") return `content: ${c.value}`
	if (c.kind === "custom") return c.value || ""
	return data?.note || ""
}

// Full prose condition for the written sections.
const conditionText = (data) => {
	const c = data?.condition
	const note = data?.note ? ` (${data.note})` : ""
	if (!c || c.kind === "always") return data?.note || "always"
	if (c.kind === "classification") return `classification = \`${c.value || "?"}\`${note}`
	if (c.kind === "content_type") return `content_types includes \`${c.value || "?"}\`${note}`
	if (c.kind === "custom") return `\`${c.value || "?"}\`${note}`
	return data?.note || "always"
}

const fence = (text, lang = "") => "```" + lang + "\n" + String(text || "").trim() + "\n```"

// Kahn topological order so the writeup reads in execution order; leftover
// nodes (cycles / disconnected) are appended in their original order.
const orderNodes = (nodes, edges) => {
	const indeg = new Map(nodes.map((n) => [n.id, 0]))
	const adj = new Map(nodes.map((n) => [n.id, []]))
	for (const e of edges) {
		if (indeg.has(e.target)) indeg.set(e.target, indeg.get(e.target) + 1)
		if (adj.has(e.source)) adj.get(e.source).push(e.target)
	}
	const queue = nodes.filter((n) => indeg.get(n.id) === 0).map((n) => n.id)
	const seen = new Set()
	const order = []
	while (queue.length) {
		const id = queue.shift()
		if (seen.has(id)) continue
		seen.add(id)
		order.push(id)
		for (const target of adj.get(id) || []) {
			indeg.set(target, indeg.get(target) - 1)
			if (indeg.get(target) <= 0 && !seen.has(target)) queue.push(target)
		}
	}
	for (const n of nodes) if (!seen.has(n.id)) order.push(n.id)
	const byId = new Map(nodes.map((n) => [n.id, n]))
	return order.map((id) => byId.get(id)).filter(Boolean)
}

const resolveAgentConfig = async (node) => {
	if (node.data?.binding === "tool") {
		const defaults = getToolById(node.data.toolId)?.defaults || {}
		try {
			const saved = await loadToolConfig(node.data.toolId)
			return { ...defaults, ...(saved || {}) }
		} catch (err) {
			return defaults
		}
	}
	return node.data?.draft || {}
}

export const buildFlowMarkdown = async ({ label, description, nodes = [], edges = [] }) => {
	const ordered = orderNodes(nodes, edges)
	const byId = new Map(nodes.map((n) => [n.id, n]))

	// Resolve agent configs in parallel up front.
	const configs = new Map()
	await Promise.all(
		ordered
			.filter((n) => n.type === "agent")
			.map(async (n) => configs.set(n.id, await resolveAgentConfig(n)))
	)

	const out = []
	const push = (...lines) => out.push(...lines)

	push(`# ${label || "Untitled flow"}`)
	if (description) push("", description)
	push(
		"",
		`_Auto-generated from the AI Tooling flow canvas. ${nodes.length} node(s), ${edges.length} connection(s)._`
	)

	// Diagram
	push("", "## Diagram", "", "```mermaid", "flowchart TD")
	for (const n of ordered) {
		push(`    ${mermaidId(n.id)}["${escapeLabel(n.data?.label || n.id)}"]`)
	}
	for (const e of edges) {
		const lbl = shortCondition(e.data)
		const arrow = lbl ? `-->|"${escapeLabel(lbl)}"|` : "-->"
		push(`    ${mermaidId(e.source)} ${arrow} ${mermaidId(e.target)}`)
	}
	push("```")

	// Steps
	push("", "## Steps")
	ordered.forEach((n, index) => {
		const typeLabel = TYPE_LABELS[n.type] || n.type
		const planned =
			n.type === "agent" &&
			(n.data?.binding === "placeholder" || n.data?.status === "planned")

		push("", `### ${index + 1}. ${n.data?.label || "Node"}`)
		push("", `- **Type:** ${typeLabel}${planned ? " (placeholder, not yet wired)" : ""}`)
		if (n.data?.description) push(`- **Description:** ${n.data.description}`)

		if (n.type === "agent") {
			const cfg = configs.get(n.id) || {}
			push(`- **Model:** \`${cfg.model || "not set"}\``)
			const tempOk = modelSupportsTemperature(cfg.model)
			push(
				`- **Temperature:** ${
					tempOk ? (cfg.temperature != null ? cfg.temperature : "not set") : "not supported by this model"
				}`
			)

			if (cfg.system_instructions) push("", "**System instructions**", "", fence(cfg.system_instructions))
			if (cfg.user_instructions) push("", "**User instructions**", "", fence(cfg.user_instructions))

			const outputs = summariseSchema(cfg.json_schema)
			if (outputs.length) {
				push("", "**Expected outputs** (from schema)")
				outputs.forEach((o) =>
					push(`- \`${o.key}\` (${o.type})${o.required ? " (required)" : ""}`)
				)
			}

			if (cfg.json_schema && Object.keys(cfg.json_schema).length) {
				const name = cfg.json_schema_name ? ` \`${cfg.json_schema_name}\`` : ""
				const strict = cfg.json_schema_strict ? " (strict)" : ""
				push("", `**JSON schema**${name}${strict}`, "", fence(JSON.stringify(cfg.json_schema, null, 2), "json"))
			}
		}

		const outEdges = edges.filter((e) => e.source === n.id)
		if (outEdges.length) {
			push("", "**Routes to:**")
			outEdges.forEach((e) => {
				const target = byId.get(e.target)
				push(`- ${target?.data?.label || e.target}: ${conditionText(e.data)}`)
			})
		}
	})

	// Flat connection list
	if (edges.length) {
		push("", "## Connections")
		edges.forEach((e) => {
			const source = byId.get(e.source)
			const target = byId.get(e.target)
			push(
				`- **${source?.data?.label || e.source} -> ${target?.data?.label || e.target}**: ${conditionText(e.data)}`
			)
		})
	}

	push("")
	return out.join("\n")
}
